import { Component, OnInit } from "@angular/core";
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from "@angular/material/dialog";
import { NavButtonComponent, SvgIconComponent } from "@evc/web-components";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { CropImageService } from "../../_services/crop-image/crop-image.service";
import { LogoImageOfflineService } from "../../_services/offline/logo-image-offline.service";
import {
  FileManagerComponent,
  FileManagerConfig,
} from "../../common-dialog/file-manager/file-manager.component";
import { NetworkStatusService } from "../../_services/network-status.service";
import { OperationMode, SelectionMode } from "../../common-dialog/file-manager/types";
import { IMAGE_FILETYPE, IMAGE_FILETYPE_MAP } from "../../app.constants";

@Component({
    selector: "app-tenant-logo",
    imports: [
        MatDialogModule,
        NavButtonComponent,
        TranslateModule,
        SvgIconComponent,
    ],
    templateUrl: "./tenant-logo.component.html",
    styleUrl: "./tenant-logo.component.scss"
})
export class TenantLogoComponent implements OnInit {
  public imageUrl: string | null = null;
  public image: File | null = null;
  private acceptedImageSize = [
    { width: 200, height: 100 },
    { width: 100, heigh: 50 },
  ];
  public isHovering: boolean = false;
  public isEdit = false;

  constructor(
    private dialog: MatDialog,
    private cropService: CropImageService,
    private logoService: LogoImageOfflineService,
    private snackbar: CommonSnackBarService,
    private translate: TranslateService,
    private networkStatusService: NetworkStatusService
  ) {}

  ngOnInit(): void {
    this.logoService.getLogoImage().subscribe((image) => {
      this.imageUrl = image;
      this.isEdit = !!image;
    });
  }

  public get useApplicationFileManager() {
    return this.networkStatusService.useApplicationFileManager();
  }

  public onFilePick(event: Event) {
    const currentFile = (event.target as HTMLInputElement)?.files?.[0];
    if (!currentFile) {
      return;
    }
    this.handleSelectedFile(currentFile);
    (event.target as HTMLInputElement).value = "";
  }

  private async handleSelectedFile(file: File) {
    const croppedImage = await this.cropService.resizeAndCropImage(
      file,
      this.acceptedImageSize[0].width,
      this.acceptedImageSize[0].height
    );
    this.image = croppedImage;
    this.imageUrl = URL.createObjectURL(croppedImage);
  }

  import() {
    if (!this.image) {
      return;
    }
    this.logoService.uploadLogoImage(this.image).subscribe(() => {
      this.snackbar.open(
        this.translate.instant("logoManagement.logoUpdatedMessage")
      );
    });
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isHovering = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleSelectedFile(files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isHovering = true;
  }

  onDragLeave(_: DragEvent) {
    this.isHovering = false;
  }

  removeLogo() {
    this.logoService.deleteLogo().subscribe(() => {
      this.imageUrl = null;
      this.image = null;
    });
  }

  showFileManager() {
    const supportedExtensions = Object.fromEntries(
      Object.entries(IMAGE_FILETYPE_MAP).filter((entry) => {
        const [extension, _mimeType] = entry;

        return [
          IMAGE_FILETYPE.EXTENSION_BMP,
          IMAGE_FILETYPE.EXTENSION_PNG,
        ].includes(extension.toLowerCase());
      })
    );

    const handleImportedSelection = (selectedFiles: File[]) => {
      if (selectedFiles.length > 0) {
        this.handleSelectedFile(selectedFiles[0]);
      }
    };

    const fileManagerConfig: MatDialogConfig<FileManagerConfig> = {
      disableClose: true,
      ...FileManagerComponent.DEFAULT_WINDOW_SIZE,
      data: {
        operationMode: OperationMode.Import,
        selectionMode: SelectionMode.SingleFile,
        extensions: supportedExtensions,
        handleImportedSelection: handleImportedSelection.bind(this),
      },
    };

    this.dialog.open(FileManagerComponent, fileManagerConfig);
  }
}
